import {
    MdAudiotrack,
    MdDescription,
    MdImage,
    MdInsertDriveFile,
    MdPictureAsPdf,
    MdTableChart,
    MdVideocam,
} from 'react-icons/md'

/** Formata bytes no padrão brasileiro (vírgula decimal). */
export function formatBytes(value: string | number): string {
    const bytes = typeof value === 'string' ? Number(value) : value
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'

    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = bytes
    let unit = 0
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024
        unit++
    }

    const decimals = unit === 0 ? 0 : size >= 10 ? 1 : 2
    return `${size.toFixed(decimals).replace('.', ',')} ${units[unit]}`
}

export function formatDateTime(value?: string | null): string {
    if (!value) return '-'
    return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export function formatDate(value?: string | null): string {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('pt-BR')
}

export function formatDuration(ms?: number | null): string {
    if (!ms) return '-'
    const totalSeconds = Math.round(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export type FileKind = 'image' | 'video' | 'audio' | 'pdf' | 'sheet' | 'document' | 'other'

export function kindOf(mimeType: string, extension?: string): FileKind {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'audio'
    if (mimeType.includes('pdf')) return 'pdf'
    if (
        mimeType.includes('sheet') ||
        mimeType.includes('excel') ||
        ['.xls', '.xlsx', '.csv'].includes(extension ?? '')
    ) {
        return 'sheet'
    }
    if (mimeType.includes('word') || mimeType.startsWith('text/')) return 'document'
    return 'other'
}

export function iconOf(mimeType: string, extension?: string) {
    switch (kindOf(mimeType, extension)) {
        case 'image':
            return MdImage
        case 'video':
            return MdVideocam
        case 'audio':
            return MdAudiotrack
        case 'pdf':
            return MdPictureAsPdf
        case 'sheet':
            return MdTableChart
        case 'document':
            return MdDescription
        default:
            return MdInsertDriveFile
    }
}

/**
 * Cor do ícone por tipo, para a grade não virar um mar de cinza.
 *
 * Tokens do tema, não a paleta crua do Tailwind: com `emerald-500` e
 * `purple-500` cravados, os ícones ficavam alheios ao tema e não acompanhavam
 * o modo escuro. O ícone já identifica o tipo — a cor é reforço, então dois
 * tipos podem dividir o mesmo tom sem prejuízo.
 */
export function colorOf(mimeType: string, extension?: string): string {
    switch (kindOf(mimeType, extension)) {
        case 'image':
            return 'text-info'
        case 'video':
            return 'text-secondary'
        case 'audio':
            return 'text-accent'
        case 'pdf':
            return 'text-error'
        case 'sheet':
            return 'text-success'
        case 'document':
            return 'text-primary'
        default:
            return 'text-base-content/40'
    }
}

/**
 * Cor da pasta. A cor escolhida pelo usuário vence; sem ela, o tom do tema
 * conforme o estado — restrita se distingue das demais à primeira vista.
 */
export function folderColorClass(folder: {
    restricted?: boolean
    color?: string | null
}): string {
    if (folder.color) return ''
    return folder.restricted ? 'text-secondary' : 'text-warning'
}

/**
 * Dimensões de uma imagem, lidas no browser antes do envio.
 *
 * Extrair aqui evita processar a imagem no servidor só para saber largura e
 * altura. Falha silenciosa é aceitável: são metadados informativos.
 */
export async function readImageDimensions(
    file: File,
): Promise<{ width: number; height: number } | null> {
    if (!file.type.startsWith('image/')) return null

    return new Promise(resolve => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
            URL.revokeObjectURL(url)
            resolve({ width: image.naturalWidth, height: image.naturalHeight })
        }
        image.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(null)
        }
        image.src = url
    })
}

/** Duração de áudio ou vídeo, lida no browser antes do envio. */
export async function readMediaDuration(file: File): Promise<number | null> {
    const isAudio = file.type.startsWith('audio/')
    const isVideo = file.type.startsWith('video/')
    if (!isAudio && !isVideo) return null

    return new Promise(resolve => {
        const url = URL.createObjectURL(file)
        const element = document.createElement(isVideo ? 'video' : 'audio')
        element.preload = 'metadata'
        element.onloadedmetadata = () => {
            URL.revokeObjectURL(url)
            resolve(Number.isFinite(element.duration) ? Math.round(element.duration * 1000) : null)
        }
        element.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(null)
        }
        element.src = url
    })
}
